import React, { useCallback, useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import { gql, useQuery } from "@apollo/client"

import useInfiniteScroll from "hooks/useInfiniteScroll"
import Layout from "components/Layout"

import cn from "classnames"
import formatDate from "utils/formatDate"
import formatMoney from "utils/formatMoney"

export const TRIPS = gql`
  query Trips(
    $filter: TripFilterInput
    $page: Int
    $size: Int
    $sort: TripSortInput
  ) {
    trips(filter: $filter, page: $page, size: $size, sort: $sort) {
      id
      name
      url
      code
      startDate
      endDate
      status

      photoURL
      image {
        url
      }

      agents {
        displayName
      }
      leaders {
        displayName
      }
    }
  }
`

const size = 30

const IndividualPage = () => {
  const history = useHistory()

  const { data, loading, error, fetchMore } = useQuery(TRIPS, {
    variables: {
      page: 0,
      size,
      sort: { startDate: "DESC" },
      filter: { type: { eq: "INDIVIDUAL" } },
    },
  })

  const hasMore = loading ? false : data?.trips?.length >= size

  const handleSearch = useCallback(
    (search) => {
      if (!fetchMore) return

      if (search?.trim() === "") {
        fetchMore({
          updateQuery: (
            previousResult,
            { fetchMoreResult, queryVariables },
          ) => {
            return {
              trips: [...fetchMoreResult.trips],
            }
          },
        })

        return
      }

      fetchMore({
        variables: { filter: { search } },
        updateQuery: (previousResult, { fetchMoreResult, queryVariables }) => {
          return {
            trips: [
              // ...previousResult.matches,
              ...fetchMoreResult.trips,
            ],
          }
        },
      })
    },
    [fetchMore],
  )

  const handleLoadMore = useCallback(
    (page) => {
      if (page === 0) return
      if (loading) return

      fetchMore({
        variables: { page },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult
          return Object.assign({}, previousResult, {
            trips: [...previousResult.trips, ...fetchMoreResult.trips],
          })
        },
      })
    },
    [fetchMore, loading],
  )

  return (
    <Layout title="Individual | GTR">
      <BoxHeader
        onSearch={handleSearch}
        onClick={() => history.push("/individual/create")}
      />

      <IndividualTable
        isLoading={loading}
        data={data?.trips}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        onSelect={(trip) => history.push(`/individual/${trip.id}`)}
      />
    </Layout>
  )
}

export default IndividualPage

const BoxHeader = ({ onSearch, onClick }) => {
  const [query, setQuery] = useState("")

  useEffect(() => {
    onSearch && onSearch(query)
  }, [query])

  return (
    <header className="bg-gray-800 text-gray-100 pb-24">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-12 gap-4">
        <div className="col-span-3 flex items-center">
          <h1 className="font-black text-3xl">Individual</h1>
        </div>

        <div className="col-span-6 relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-4">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 fill-current text-gray-600"
            >
              <path d="M16.32 14.9l1.1 1.1c.4-.02.83.13 1.14.44l3 3a1.5 1.5 0 0 1-2.12 2.12l-3-3a1.5 1.5 0 0 1-.44-1.14l-1.1-1.1a8 8 0 1 1 1.41-1.41l.01-.01zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z" />
            </svg>
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            className="block w-full border border-transparent bg-gray-900 focus:outline-none focus:bg-white focus:border-gray-300 text-gray-900 rounded-lg pl-12 pr-4 py-2"
          />
        </div>

        <div className="col-span-3 flex justify-end items-center pl-8">
          <button
            onClick={onClick}
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Individual
          </button>
        </div>
      </div>
    </header>
  )
}

const IndividualTable = ({
  isLoading,
  data,
  hasMore,
  onLoadMore,
  onSelect,
}) => {
  const header = [
    { name: "ID", width: "w-3/12" },
    { name: "Date", width: "w-3/12" },
    { name: "Status", width: "w-3/12" },
    { name: "Agent", width: "w-3/12" },
  ]

  const getPaid = (payments) =>
    payments
      ?.filter((v) => v.status === "PAID")
      ?.reduce((acc, val) => acc + val.amount, 0)

  const roundPercentage = (data) => {
    const arr = data?.map((v) => v.amount)
    const total = arr?.reduce((a, b) => a + b, 0)
    let percentage = arr?.map((v) => Math.floor((Math.abs(v) / total) * 100))
    const totalPercentage = percentage.reduce((a, b) => a + b, 0)
    // const minPercetange = Math.min(...percentage)

    let diffrence = 0
    percentage = percentage.map((percent, i) =>
      percent < 10 ? percent + (20 - percent) : percent,
    )

    if (totalPercentage !== 100) {
      const lowest = percentage.indexOf(Math.min(...percentage))
      const percToAdd = 100 - totalPercentage
      percentage = percentage.map((v, i) => (i === lowest ? v + percToAdd : v))
    }

    // const paidPerc =
    // const paidWidth = Math.floor(paidPerc)

    // booking.progress
    return data.map((v, i) => ({
      ...v,
      // width: `${Math.floor((Math.abs(v.amount) / total) * 100)}%`,
      width: `${percentage[i]}%`,
      // width: Math.floor(
      //   Math.abs(Math.floor(Math.abs(v.paidAmount / v.amount) * 100)),
      // ),
    }))
  }

  const bars = (data) => roundPercentage(data)

  const [page, loaderRef] = useInfiniteScroll({ hasMore })

  useEffect(() => {
    onLoadMore && onLoadMore(page)
  }, [page])

  return (
    <main className="max-w-screen-xl mx-auto md:px-4">
      <div className="relative -mt-24 z-30">
        <div className="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200 mt-8">
          {isLoading ? (
            <div className="bg-white rounded-lg shadow-lg">
              <div className="py-16 flex flex-col justify-center items-center bg-wh">
                <div>...Loading...</div>
              </div>
            </div>
          ) : (
            <div style={{ maxHeight: "70vh", overflowY: "scroll" }}>
              <table className="min-w-full table-auto">
                <thead>
                  <tr>
                    {header.map((item, index) => (
                      <th
                        key={index}
                        className="px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                        style={{ position: "sticky", top: "0" }}
                      >
                        {item.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {data?.map((trip, index) => (
                    <tr
                      key={index}
                      onClick={() => onSelect(trip)}
                      className="hover:bg-blue-100 cursor-pointer"
                    >
                      <td className="w-3/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
                        <div className="flex items-center">
                          {/* <div className="flex-shrink-0 h-10 w-10">
                            <Avatar
                              name={trip?.user?.displayName}
                              // src={user.photoURL}
                              size="40"
                              round={true}
                            />
                          </div> */}
                          <div>
                            <div className="text-sm leading-5 text-gray-900">
                              {trip?.code}
                            </div>
                            {/* <div className="text-sm leading-5 text-gray-500">
                              {trip?.name}
                            </div> */}
                          </div>
                        </div>
                      </td>
                      <td className="w-3/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
                        <p className="inline-flex text-xs leading-5 font-semibold">
                          {`${formatDate(
                            trip.startDate,
                            "dd. MMM",
                          )} - ${formatDate(trip.endDate, "dd. MMM yyyy")}`}
                        </p>
                      </td>
                      <td className="w-3/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-500">
                        <span
                          className={cn(
                            `px-2 inline-flex text-xs leading-5 font-semibold rounded-full`,
                            {
                              [`bg-green-100 text-green-800`]:
                                trip.status === "COMPLETED" ||
                                trip.status === "FINISHED",
                              [`bg-yellow-100 text-yellow-800`]:
                                trip.status === "CREATED",
                              [`bg-red-100 text-red-800`]:
                                trip.status === "CANCELED" ||
                                "CANCELED_BY_SYSTEM",
                              [`bg-blue-100 text-blue-800`]:
                                trip.status === "IN_PROGRESS",
                            },
                          )}
                        >
                          {trip.status}
                        </span>
                      </td>
                      <td className="w-3/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 font-medium">
                        <div className="text-sm leading-5 text-gray-900">
                          {trip?.agents.map((agent, index) => (
                            <p>{agent.displayName}</p>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {hasMore && (
                <div ref={loaderRef} className="text-center bg-gray-100">
                  Loading…
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  )
}
